<template>
  <!-- Worker nodes typology -->
  <v-row class="ma-0 mx-4 mt-4 mb-2 token-text-h5">
    Select the typology of the worker nodes for your new pool
  </v-row>
  <v-row class="ma-0 mx-4 mb-2">
    <v-col class="pa-0 mr-2 fit-content" v-for="tag of tagList" :key="'tag-'+tag">
      <Badge :label="tag" :severity="$enums.Severity.NEUTRAL" :outlined="!selectedTags.includes(tag)" @click="onClickTag(tag)" class="tag"/>
    </v-col>
  </v-row>
  <v-row class="ma-0 mx-4 mb-2">
    <v-col class="pa-0" v-for="(typology, index) of nodeTypologies" :key="'typo-' + index" cols="6" >
      <div v-if="!isTypoAbsent(typology)"
        class="card-typo"
        :class="(index%2 === 0 ? ' mr-2 ' : ' ml-2 ') + (selectedTypology?.id === typology.id ? ' selected ' : '') + (isTypoDisabled(typology) ? ' disabled ' : '')"
        @click="onClickTypoCard(typology)"
      >
        <v-row class="ma-0 mb-2">
          <v-col class="pa-0 fit-content">
            {{ typology.attributes.name }}
          </v-col>
          <v-col class="pa-0 ml-auto fit-content">
            <PriceDisplay
              :price="location && typology.attributes.price ? typology.attributes.price.amount : null"
              :currency="currency"
              :hidePeriodicity="true"
            />
          </v-col>
        </v-row>
        <v-row class="ma-0 token-text-color-secondary">
          {{ typology.attributes.cores }} {{ typology.attributes.dedicated_cpu ? ' dedicated ' : ' shared ' }} CPUs
        </v-row>
        <v-row class="ma-0 token-text-color-secondary">
          {{ typology.attributes.memory }} RAM
        </v-row>
        <v-row class="ma-0 token-text-color-secondary">
          <template v-if="typology.attributes.cpu_family">
            {{ typology.attributes.cpu_family }}
          </template>
          <template v-else>
            &nbsp;
          </template>
        </v-row>
        <v-row class="ma-0 mt-1">
          <v-col class="pa-0 mr-2 fit-content" v-for="tag of typology.attributes.tags" :key="typology?.id+'-tag-'+tag">
            <Badge :label="tag" :severity="$enums.Severity.NEUTRAL" :outlined="!selectedTags.includes(tag)" @click="onClickTag(tag)" class="tag"/>
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { APIClusterLocation, APIClusterNodeTypologie } from '@/typesAPI';
import { Vue, Options, prop } from 'vue-class-component'
import { Watch } from 'vue-property-decorator';

import PriceDisplay from '@/components/UIElements/PriceDisplay.vue'
import Badge from '@/components/UIElements/Badge.vue'

class Props {
  provider: string = prop({
    required: true,
  });
  location: APIClusterLocation = prop({
    required: true,
  });
}


@Options({
  components: {
    PriceDisplay,
    Badge,
  },
})
export default class MPCAddNodePool extends Vue.with(Props) {
  selectedTags:string[] = []
  selectedTypology:APIClusterNodeTypologie | null = null;

  get tagList():string[] {
    const ret:string[] = []
    this.nodeTypologies?.forEach((clusterNodeTypologie:APIClusterNodeTypologie) => {
      ret.push(...clusterNodeTypologie.attributes.tags)
    })
    return [...new Set(ret)]
  }

  get nodeTypologies():APIClusterNodeTypologie[] {
    if(!this.provider) {
      return []
    }
    return this.$store.getters['clusters/getClusterNodeTypologiesByProviderAndLocation'](this.provider, this.location?.id)
  }

  @Watch('tagList', { immediate: true })
  onTagListChange() {
    // Reset selected tags
    this.selectedTags = [...this.tagList]
  }

  onClickTag(tag:string) {
    const index = this.selectedTags.indexOf(tag);
    if (index > -1) {
      this.selectedTags.splice(index, 1);
    } else {
      this.selectedTags.push(tag)
      this.selectedTags = [...new Set(this.selectedTags)]
    }

  }

  isTypoAbsent(typology:APIClusterNodeTypologie) {
    const isTaged = this.selectedTags.some((tag:string) => {
      if(typology?.attributes.tags.includes(tag)) {
        return true
      }
      return false
    })
    return !isTaged || !(this.location?.id && typology?.attributes.price)
  }

  isTypoDisabled(typology:APIClusterNodeTypologie) {
    if(this.location) {
      if(this.location.attributes.available_server_typologies.find((typoName:string) => typoName === typology.id)) {
        return false
      }
    }
    return true
  }

  get currency():string {
    return this.$store.getters['user/getCurrency']
  }

  onClickTypoCard(typology:APIClusterNodeTypologie) {
    if(!this.isTypoAbsent(typology) && !this.isTypoDisabled(typology)) {
      this.selectedTypology = typology
      this.$emit('typologySelected', typology)
    }
  }
 

}
</script>

<style lang="scss" scoped>
@import '@/css/variables';
.card-typo {
  border:1px solid $color-neutral-grey-12;
  border-radius: 8px;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  padding: 12px;
  margin-bottom: 16px;
  cursor: pointer;
  &:hover {
    border: 1px solid $color-neutral-grey-24;
    box-shadow: none;
  }
  
  &.disabled {
    border:1px solid $color-neutral-grey-12;
    background: $color-neutral-grey-12;
    cursor: default !important;
  }
  &.selected {
    padding-top: 11px;
    padding-left: 11px;
    margin-bottom: 15px;
    border: 2px solid $color-brand;
    box-shadow: 0px 0px 0px 2px rgba(50, 25, 178, 0.24);
  }
}
.tag {
  cursor: pointer;
}
</style>